import { useState } from "react";
import { Truck, Pencil, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import type { Policy, PolicyVehicle } from "@shared/schema";
import { AssetSectionHeader } from "./AssetSectionHeader";
import { AssetStatusBadge, statusForAsset } from "./AssetStatusBadge";
import { formatMakeModel, vehicleValue, type VehicleEntry } from "./asset-helpers";
import { VehicleDialog } from "./VehicleDialog";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { useUpdatePolicyVehicles } from "@/lib/api";

interface Props {
  powerUnits: VehicleEntry[];
  policies: Policy[];
}

const HEAD_CLASS = "px-6 py-3 text-xs uppercase tracking-wider text-muted-foreground";
const CELL_CLASS = "px-6 py-5";

export function PowerUnitsSection({
  powerUnits,
  policies,
}: Props) {
  const { toast } = useToast();
  const { confirm, confirmDialog } = useConfirm();
  const updateVehicles = useUpdatePolicyVehicles();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editData, setEditData] = useState<PolicyVehicle | null>(null);
  const [editPolicyId, setEditPolicyId] = useState<string | null>(null);

  function handleAdd() {
    setEditData(null);
    setEditPolicyId(null);
    setDialogOpen(true);
  }

  function handleEdit(entry: VehicleEntry) {
    setEditData(entry.vehicle);
    setEditPolicyId(entry.policyId);
    setDialogOpen(true);
  }

  async function handleRemove(entry: VehicleEntry) {
    const label = entry.vehicle.vin ? `VIN ${entry.vehicle.vin}` : "this unit";
    const ok = await confirm({
      title: "Remove Power Unit",
      description: `Remove ${label} from the policy?`,
      confirmLabel: "Remove",
      destructive: true,
    });
    if (!ok) return;
    const policy = policies.find((p) => p.id === entry.policyId);
    if (!policy) return;
    const current = (policy.vehicles as PolicyVehicle[] | null) ?? [];
    const next = current.filter(
      (v) => !(v.vin === entry.vehicle.vin && v.addedAt === entry.vehicle.addedAt),
    );
    try {
      await updateVehicles.mutateAsync({ id: policy.id, vehicles: next });
      toast({ title: "Removed" });
    } catch (err) {
      toast({
        title: "Failed to remove",
        description: err instanceof Error ? err.message : undefined,
        variant: "destructive",
      });
    }
  }

  return (
    <Card className="overflow-hidden" data-testid="power-units-section">
      <AssetSectionHeader
        icon={Truck}
        title="Power Units"
        count={powerUnits.length}
        testId="power-units"
        onAdd={policies.length > 0 ? handleAdd : undefined}
        disabledTooltip={policies.length === 0 ? "This insured has no policy yet — assets are stored on a policy, so add one first" : undefined}
      />
      {powerUnits.length === 0 ? (
        <div
          className="px-6 py-12 text-center text-sm text-muted-foreground"
          data-testid="power-units-empty"
        >
          No power units on record.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className={HEAD_CLASS}>VIN</TableHead>
                <TableHead className={HEAD_CLASS}>Make / Model</TableHead>
                <TableHead className={HEAD_CLASS}>Year</TableHead>
                <TableHead className={HEAD_CLASS}>Body Type</TableHead>
                <TableHead className={HEAD_CLASS}>Value</TableHead>
                <TableHead className={HEAD_CLASS}>Radius</TableHead>
                <TableHead className={HEAD_CLASS}>Status</TableHead>
                <TableHead className={HEAD_CLASS}></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {powerUnits.map((entry, i) => (
                <TableRow
                  key={entry.vehicle.vin ?? `power-unit-${i}`}
                  data-testid={`power-unit-row-${entry.vehicle.vin ?? i}`}
                >
                  <TableCell className={`${CELL_CLASS} font-mono text-sm text-foreground`}>
                    {entry.vehicle.vin || "—"}
                  </TableCell>
                  <TableCell className={`${CELL_CLASS} font-medium`}>
                    {formatMakeModel(entry.vehicle)}
                  </TableCell>
                  <TableCell className={`${CELL_CLASS} text-muted-foreground`}>
                    {entry.vehicle.year ?? "—"}
                  </TableCell>
                  <TableCell className={`${CELL_CLASS} text-muted-foreground`}>
                    {entry.vehicle.truckType || "—"}
                  </TableCell>
                  <TableCell className={`${CELL_CLASS} text-muted-foreground`}>
                    {vehicleValue(entry.vehicle)}
                  </TableCell>
                  <TableCell
                    className={`${CELL_CLASS} text-muted-foreground`}
                    data-testid={`power-unit-radius-${entry.vehicle.vin ?? i}`}
                  >
                    {entry.vehicle.radius || "—"}
                  </TableCell>
                  <TableCell className={CELL_CLASS}>
                    <AssetStatusBadge status={statusForAsset(entry.removedAt, entry.active)} />
                  </TableCell>
                  <TableCell className={CELL_CLASS}>
                    <div
                      className="flex items-center gap-1"
                      data-testid={`power-unit-row-action-${entry.vehicle.vin ?? i}`}
                    >
                      <Button
                        size="sm"
                        variant="ghost"
                        className="cursor-pointer"
                        aria-label={`Edit VIN ${entry.vehicle.vin ?? ""}`}
                        disabled={updateVehicles.isPending}
                        onClick={() => handleEdit(entry)}
                        data-testid={`power-unit-edit-${entry.vehicle.vin ?? i}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="cursor-pointer text-destructive"
                        aria-label={`Remove VIN ${entry.vehicle.vin ?? ""}`}
                        disabled={updateVehicles.isPending}
                        onClick={() => handleRemove(entry)}
                        data-testid={`power-unit-remove-${entry.vehicle.vin ?? i}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <VehicleDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        mode="powerUnit"
        policies={policies}
        editData={editData}
        editPolicyId={editPolicyId}
      />
      {confirmDialog}
    </Card>
  );
}
